import { Modal, Span } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import useNotifications from 'core/notifications/useNotifications';
import { useRcsTemplateEditorDispatch, useRcsTemplateEditorState } from '../../context/useRcsTemplateEditorContext';
import { useSaveRcsTemplate } from '../../hooks/useSaveRcsTemplate';
import { rcsConfigToCompilerTemplateValue } from '../../utils/rcsConfigToCardContent';
import { hasValidationErrors, validateRcsConfig } from '../../utils/validateRcsConfig';

interface IProps {
  modalOpen: boolean;
  setModalOpen(isOpen: boolean): void;
  onSave?(): void;
  onDiscard?(): void;
}

export const RcsTemplateEditorSaveUnsavedChangesModal = ({ modalOpen, setModalOpen, onSave, onDiscard }: IProps) => {
  const { t } = useTranslation(['designs']);
  const { showNotification } = useNotifications();
  const dispatch = useRcsTemplateEditorDispatch();
  const { rcsConfig, templateName, templateType, selectedLanguage, enrichmentConfigIds } = useRcsTemplateEditorState();
  const { saveMutation, siteId, templateId, routeLanguage } = useSaveRcsTemplate();

  const handleClose = () => {
    setModalOpen(false);
  };

  const handleDiscard = () => {
    dispatch({ type: 'DISCARD_CHANGES' });
    onDiscard?.();
    setModalOpen(false);
  };

  const handleSave = () => {
    const validationErrors = validateRcsConfig(rcsConfig);
    if (hasValidationErrors(validationErrors)) {
      dispatch({ type: 'SET_RCS_VALIDATION_ERRORS', payload: { validationErrors } });
      return;
    }
    if (!siteId || !templateId || !routeLanguage) {
      showNotification(t('designs:designGallery.templateEditor.queries.save.error'), 'error');
      return;
    }
    const templateBody = rcsConfigToCompilerTemplateValue(rcsConfig);
    saveMutation.mutate(
      {
        siteId,
        templateId,
        templateName,
        templateBody,
        language: selectedLanguage || routeLanguage,
        type: templateType,
        enrichmentConfigIds: enrichmentConfigIds ?? [],
        rcsConfigSnapshot: rcsConfig,
        t,
      },
      {
        onSuccess: () => {
          onSave?.();
          setModalOpen(false);
        },
      },
    );
  };

  return (
    <Modal
      heading={t('designs:designGallery.templateEditor.modals.unsavedChanges.header')}
      variation="clean"
      isOpen={modalOpen}
      onCancelButtonClicked={handleDiscard}
      onCloseButtonClicked={handleClose}
      onOkButtonClicked={handleSave}
      okButtonLabel={t('designs:designGallery.templateEditor.modals.unsavedChanges.confirm')}
      okButtonDisabled={saveMutation.isLoading}
      cancelButtonLabel={t('designs:designGallery.templateEditor.modals.unsavedChanges.cancel')}
    >
      <Span>{t('designs:designGallery.templateEditor.modals.unsavedChanges.previewMessage')}</Span>
    </Modal>
  );
};
